import { ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { IMGS } from "@/data";
import { useLang } from "@/i18n";

const About = () => {
  const { t } = useLang();

  return (
    <div className="min-h-screen bg-bone font-sans text-ink antialiased">
      <Seo title={t.seo.about.title} description={t.seo.about.description} />
      <Header />
      <main className="pt-[210px] md:pt-[240px]">
        <section className="mx-auto max-w-7xl px-6 md:px-12" data-testid="about-section">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-clay">{t.about.overline}</p>
            <h1 data-testid="about-heading" className="mt-6 max-w-3xl font-serif text-5xl leading-[1.05] tracking-tight text-ink sm:text-6xl">
              {t.about.h1a} <em className="italic text-clay">{t.about.h1b}</em>
            </h1>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-14 lg:grid-cols-12">
            <Reveal delay={0.1} className="lg:col-span-6">
              <p className="text-base leading-relaxed text-ink/70 md:text-lg">{t.about.p1}</p>
              <p className="mt-6 text-base leading-relaxed text-ink/70 md:text-lg">{t.about.p2}</p>
              <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden border border-ink/10 bg-ink/10 sm:grid-cols-3">
                {t.about.values.map((v, i) => (
                  <div key={v.t} data-testid={`about-value-${i + 1}`} className="bg-bone p-7">
                    <p className="font-serif text-xl tracking-tight text-clay">{v.t}</p>
                    <p className="mt-3 text-sm leading-relaxed text-ink/60">{v.b}</p>
                  </div>
                ))}
              </div>
              <a href="/#quote" data-testid="about-cta" className="group mt-10 inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-bone transition-colors duration-300 hover:bg-clay">
                {t.about.cta}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180" />
              </a>
            </Reveal>
            <Reveal delay={0.2} className="lg:col-span-6">
              <div className="group overflow-hidden bg-sand">
                <img
                  src={IMGS.factory}
                  alt="The HHB Clothing Co production floor"
                  data-testid="about-factory-image"
                  className="aspect-[4/3] w-full object-cover grayscale-[30%] transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
                />
              </div>
              <div className="mt-6 grid grid-cols-2 gap-6">
                <div className="group overflow-hidden bg-sand">
                  <img src={IMGS.pattern} alt="Pattern making at HHB Clothing Co" loading="lazy" className="aspect-square w-full object-cover grayscale-[30%] transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0" />
                </div>
                <div className="group overflow-hidden bg-sand">
                  <img src={IMGS.packing} alt="Garments being packed at HHB Clothing Co" loading="lazy" className="aspect-square w-full object-cover grayscale-[30%] transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0" />
                </div>
              </div>
            </Reveal>
          </div>
        </section>
        <div className="h-24 md:h-32" />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
};

export default About;
